import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './OpenOsV2ProductionChart.css';

const CHART_HEIGHT = 148;
const TIP_PAD = 10;
const TIP_EST_W = 260;
const TIP_EST_H = 120;

const formatContador = (val) => {
  if (val == null || val === '') return '—';
  const n = typeof val === 'number' ? val : Number(val);
  return Number.isNaN(n) ? String(val) : n.toLocaleString('pt-BR');
};

const formatChartAxisTick = (val) => {
  if (val == null || val === '') return '';
  const n = typeof val === 'number' ? val : Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

/** z-index acima do overlay que contém o gráfico (Abrir OS ~20050 / Detalhes OS hist ~27000). */
function getTooltipZIndex(chartEl) {
  const overlay = chartEl?.closest?.('.open-os-v2-mock-overlay');
  if (overlay) {
    const z = parseInt(window.getComputedStyle(overlay).zIndex, 10);
    if (Number.isFinite(z)) return z + 100;
  }
  return 21000;
}

/** Posiciona tooltip em fixed, dentro do viewport / hist-body — sem expandir scroll. */
function getFixedTooltipStyle(coordinate, chartEl) {
  if (!coordinate || !chartEl) return { display: 'none' };

  const rect = chartEl.getBoundingClientRect();
  const body = chartEl.closest('.oosv2-hist-body') || document.querySelector('.oosv2-hist-body');
  const bound = body?.getBoundingClientRect() || {
    left: TIP_PAD,
    top: TIP_PAD,
    right: window.innerWidth - TIP_PAD,
    bottom: window.innerHeight - TIP_PAD,
  };

  const anchorX = rect.left + coordinate.x;
  const anchorY = rect.top + coordinate.y;
  const above = coordinate.y > Math.max(56, rect.height * 0.38);

  let left = anchorX;
  let top = anchorY;
  let tx = '-50%';
  let ty = above ? 'calc(-100% - 8px)' : '8px';

  const halfW = TIP_EST_W / 2;
  if (anchorX + halfW > bound.right - TIP_PAD) {
    left = Math.min(anchorX, bound.right - TIP_PAD);
    tx = '-100%';
  } else if (anchorX - halfW < bound.left + TIP_PAD) {
    left = Math.max(anchorX, bound.left + TIP_PAD);
    tx = '0';
  }

  if (above && anchorY - TIP_EST_H < bound.top + TIP_PAD) {
    ty = '8px';
  } else if (!above && anchorY + TIP_EST_H > bound.bottom - TIP_PAD) {
    ty = 'calc(-100% - 8px)';
  }

  return {
    position: 'fixed',
    left,
    top,
    transform: `translate(${tx}, ${ty})`,
    zIndex: getTooltipZIndex(chartEl),
  };
}

function ProductionTooltip({ active, payload, label, coordinate }) {
  const chartEl =
    typeof document !== 'undefined'
      ? document.querySelector('.oosv2-recharts-canvas .recharts-wrapper')
      : null;

  if (!active || !payload?.length || typeof document === 'undefined') return null;

  const total = payload.reduce((acc, entry) => acc + (Number(entry.value) || 0), 0);
  const style = getFixedTooltipStyle(coordinate, chartEl);
  const portalRoot =
    chartEl?.closest?.('.open-os-v2-mock-overlay') || document.body;

  return createPortal(
    <div className="oosv2-recharts-tooltip oosv2-recharts-tooltip--portal" style={style}>
      <div className="oosv2-recharts-tooltip-date">{label}</div>
      <div className="oosv2-recharts-tooltip-total">Total {formatContador(total)}</div>
      {payload.map((entry) => {
        const medRaw = entry.payload?.[`${entry.dataKey}__med`];
        const medN = medRaw == null || medRaw === '' ? null : Number(medRaw);
        const medOk = medN != null && !Number.isNaN(medN);
        return (
          <div key={entry.dataKey} className="oosv2-recharts-tooltip-row">
            <span
              className="oosv2-recharts-tooltip-dot"
              style={{ background: entry.color }}
            />
            <span>
              {entry.name}: {formatContador(entry.value)}
              {medOk ? (
                <span className="oosv2-recharts-tooltip-med">
                  {' '}
                  ({formatContador(medN)})
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>,
    portalRoot,
  );
}

/**
 * Área de produção (Recharts) montada no host do mock Abrir OS v2.
 * @param {{ meses: string[], series: Array<{key:string,label:string,cor:string,valores:number[],medAtual?:(number|null)[]}>, activeKeys: string[], cicloMensal?: number|null }} props
 */
export default function OpenOsV2ProductionChart({
  meses = [],
  series = [],
  activeKeys = [],
  cicloMensal = null,
}) {
  const wrapRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  const cicloOk = useMemo(() => {
    const n = cicloMensal != null ? Math.trunc(Number(cicloMensal)) : null;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [cicloMensal]);

  const activeSeries = useMemo(
    () => series.filter((se) => activeKeys.includes(se.key)),
    [series, activeKeys],
  );

  const chartData = useMemo(() => {
    const labels = Array.isArray(meses) ? meses : [];
    return labels.map((mes, i) => {
      const row = { mes };
      activeSeries.forEach((se) => {
        row[se.key] = Number(se.valores?.[i]) || 0;
        const med = se.medAtual?.[i];
        row[`${se.key}__med`] =
          med == null || med === '' || Number.isNaN(Number(med)) ? null : Number(med);
      });
      return row;
    });
  }, [meses, activeSeries]);

  const chartYMax = useMemo(() => {
    let max = cicloOk || 0;
    chartData.forEach((row) => {
      const total = activeSeries.reduce((acc, se) => acc + (Number(row[se.key]) || 0), 0);
      if (total > max) max = total;
    });
    if (max <= 0) return 0;
    return Math.ceil(max * 1.12);
  }, [chartData, activeSeries, cicloOk]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.floor(el.getBoundingClientRect().width);
      setChartWidth((prev) => (prev === w ? prev : w > 0 ? w : 0));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [chartData.length, activeSeries.length, cicloOk]);

  const hasProduction = useMemo(() => {
    if (!chartData.length || !activeSeries.length) return false;
    return chartData.some((row) =>
      activeSeries.some((se) => Number(row[se.key]) > 0),
    );
  }, [chartData, activeSeries]);

  if (!activeSeries.length) {
    return (
      <div className="oosv2-recharts-empty" role="status">
        <p className="oosv2-recharts-empty-title">Nenhuma série selecionada</p>
        <p className="oosv2-recharts-empty-text">Selecione ao menos um contador acima.</p>
      </div>
    );
  }

  if (!chartData.length || !hasProduction) {
    return (
      <div className="oosv2-recharts-empty" role="status">
        <div className="oosv2-recharts-empty-icon" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M4 19V5M4 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M8 15v-3M12 15V9M16 15v-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </div>
        <p className="oosv2-recharts-empty-title">Sem produção no período</p>
        <p className="oosv2-recharts-empty-text">Não há produção registrada neste período</p>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="oosv2-recharts-canvas">
      {chartWidth > 0 ? (
        <AreaChart
          width={chartWidth}
          height={CHART_HEIGHT}
          data={chartData}
          margin={{ top: 14, right: 8, left: 4, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--oosv2-chart-grid, #e2e8f0)" vertical={false} />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 9, fill: 'var(--oosv2-chart-tick, #64748b)' }}
            axisLine={{ stroke: 'var(--oosv2-chart-grid, #e2e8f0)' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 9, fill: 'var(--oosv2-chart-tick, #64748b)' }}
            axisLine={false}
            tickLine={false}
            width={56}
            domain={chartYMax ? [0, chartYMax] : ['auto', 'auto']}
            tickFormatter={formatChartAxisTick}
          />
          {cicloOk != null ? (
            <ReferenceLine
              y={cicloOk}
              stroke="#f59e0b"
              strokeDasharray="5 4"
              strokeWidth={1.5}
              label={{
                value: formatContador(cicloOk),
                position: 'insideTopRight',
                fill: '#b45309',
                fontSize: 10,
                fontWeight: 600,
              }}
            />
          ) : null}
          <Tooltip
            content={<ProductionTooltip />}
            cursor={{ stroke: '#7c4dff', strokeWidth: 1.2, strokeDasharray: '3 3' }}
            isAnimationActive={false}
            allowEscapeViewBox={{ x: false, y: false }}
            wrapperStyle={{
              outline: 'none',
              pointerEvents: 'none',
              visibility: 'hidden',
              width: 0,
              height: 0,
              overflow: 'hidden',
            }}
          />
          {activeSeries.map((se) => (
            <Area
              key={se.key}
              type="monotone"
              dataKey={se.key}
              name={se.label}
              stackId="a"
              stroke={se.cor}
              fill={se.cor}
              fillOpacity={0.35}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            />
          ))}
        </AreaChart>
      ) : null}
    </div>
  );
}
